import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ErrorToastBlacklistService } from '@lsq-frontend/angular-shared/features/error-toast-blacklist-service';
import {
    FeatureFlagService,
    ResponsiveService,
    WindowDimensions,
    WindowReferenceService
} from '@lsq-frontend/common-ui';
import {
    GetAnalyticsReportUrlRequest,
    GetAnalyticsReportUrlService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer/insights/get-analytics-report-url';
import { ResponseCode } from '@lsq-frontend/remote-services/enterprise/common';
import { concat, Observable, of } from 'rxjs';
import { debounceTime, first, map, skip, switchMap } from 'rxjs/operators';
import { TABLEAU_EMBED_QUERY_PARAMS_DEFAULT } from './insights.configuration';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';

export interface TableauIFrameParams {
    url: SafeResourceUrl;
    heightPx: number;
}

@Injectable({ providedIn: 'root' })
export class BuyerInsightsService {
    constructor(
        private _errorToastBlacklistService: ErrorToastBlacklistService,
        private _getAnalyticsReportUrlService: GetAnalyticsReportUrlService,
        public responsiveService: ResponsiveService,
        private _domSanitizer: DomSanitizer,
        private _windowReferenceService: WindowReferenceService,
        private _featureFlagService: FeatureFlagService
    ) {
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            this._getAnalyticsReportUrlService.endpointUrl,
            ResponseCode.Forbidden
        );
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            this._getAnalyticsReportUrlService.endpointUrl,
            ResponseCode.Unauthorized
        );
        // FIXME: Temporary fix because mk2 throws 500 when it means to throw a 401.
        // (SecurityAspect throws an HttpClientErrorException, but LsqExceptionHandler expects an AccessDeniedException.)
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            this._getAnalyticsReportUrlService.endpointUrl,
            ResponseCode.Error
        );
    }
    public highChartsIsOn = () =>
        this._featureFlagService.isOnSync(FeatureFlags.BuyerExperienceHighCharts);

    public async isActivatedForBuyer(): Promise<boolean> {
        let isActivated = false;
        if (this.highChartsIsOn()) {
            return true;
        }

        try {
            // FIXME: This is a shortcut to determine whether a buyer has access to Tableau.
            // If the call succeeds, we know they have access; if it fails, we assume it's
            // because they don't.
            await this._getAnalyticsReportUrlService.executeOnce(
                new GetAnalyticsReportUrlRequest({ reportName: 'SPEND_VOLUME' })
            );
            isActivated = true;
        } catch {
            // The buyer doesn't have access; isActivated is already false, so do nothing.
        }

        return isActivated;
    }

    public async workingCapitalLiftIsActivatedForBuyer(): Promise<boolean> {
        let canViewWorkingCapitalLift = false;
        if (this.highChartsIsOn()) {
            return true;
        }

        try {
            // Check if buyer can view working capital lift
            const result = await this._getAnalyticsReportUrlService.executeOnce(
                new GetAnalyticsReportUrlRequest({ reportName: 'WORKING_CAPITAL_LIFT' })
            );
            if (result) canViewWorkingCapitalLift = !!result.canView;
        } catch {
            // The buyer doesn't have access; canViewWorkingCapitalLift is already false, so do nothing.
        }

        return canViewWorkingCapitalLift;
    }

    public getIFrameProps$(
        reportName: string,
        desktopHeightPx: number,
        phoneHeightPx: number
    ): Observable<TableauIFrameParams> {
        if (this.highChartsIsOn()) {
            return of({
                url: '',
                heightPx: 0
            });
        }
        return concat([
            this._windowReferenceService.resizeStream.pipe(first()),
            this._windowReferenceService.resizeStream.pipe(skip(1), debounceTime(1000))
        ]).pipe(
            switchMap((dimensions$) => dimensions$),
            switchMap((dimensions) =>
                this._getAnalyticsReportUrlService
                    .executeOnce(new GetAnalyticsReportUrlRequest({ reportName }))
                    .then(({ reportUrl }) => [reportUrl, dimensions] as [string, WindowDimensions])
            ),
            map(([reportUrl, dimensions]) => {
                let device = 'desktop';
                if (dimensions.width < 1280) {
                    device = 'phone';
                }
                return {
                    url: this._domSanitizer.bypassSecurityTrustResourceUrl(
                        reportUrl + `${TABLEAU_EMBED_QUERY_PARAMS_DEFAULT}&:device=${device}`
                    ),
                    heightPx: device === 'desktop' ? desktopHeightPx : phoneHeightPx ?? 850
                };
            })
        );
    }
}
